import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Globe } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { SITE_CONFIG } from "../config";
import { trackWhatsAppClick } from "../hooks/useAnalytics";

const LOGO_URL = "/assets/logo-circle.svg";

export const Header = () => {
  const { t, i18n } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { path: "/", label: t('nav.inicio') },
    { path: "/portfolio", label: t('nav.portfolio') },
    { path: "/servicios", label: t('nav.servicios') },
    { path: "/planes", label: t('nav.planes') },
    { path: "/blog", label: t('nav.blog') },
    { path: "/contacto", label: t('nav.contacto') }
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const toggleLanguage = () => {
    const newLang = i18n.language === 'es' ? 'en' : 'es';
    i18n.changeLanguage(newLang);
    localStorage.setItem('language', newLang);
  };

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-500 bg-white/70 backdrop-blur-xl border-b border-[#1C2E24]/8 py-4"
      data-testid="main-header"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
          <img
            src={LOGO_URL}
            alt="Circle"
            className="h-10 w-auto"
            style={{ filter: 'invert(1)' }}
          />
          <div className="flex flex-col leading-none">
            <span className="text-[#1C2E24] font-light tracking-[0.3em] text-sm uppercase">Circle</span>
            <span className="text-[#547055] text-[10px] tracking-[0.15em] uppercase">Natural Editing</span>
          </div>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`nav-link ${
                location.pathname === link.path ? "text-[#1C2E24]" : ""
              }`}
              data-testid={`nav-${link.label.toLowerCase()}`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        {/* Right side: Language + CTA */}
        <div className="hidden lg:flex items-center gap-4">
          {/* Language Toggle */}
          <button
            onClick={toggleLanguage}
            className="flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-wider text-[#547055] hover:text-[#1C2E24] border border-[#E6E2D6] hover:border-[#849C8A] transition-colors"
            data-testid="language-toggle"
          >
            <Globe size={14} />
            {i18n.language === 'es' ? 'EN' : 'ES'}
          </button>

          {/* CTA Button */}
          <a
            href={`https://wa.me/${SITE_CONFIG.whatsapp.number}?text=${encodeURIComponent(SITE_CONFIG.whatsapp.appointmentMessage)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary"
            data-testid="header-cta"
            onClick={() => trackWhatsAppClick("header_cta")}
          >
            {t('nav.agendarConsulta')}
          </a>
        </div>

        {/* Mobile: Language + Menu Button */}
        <div className="lg:hidden flex items-center gap-3">
          <button
            onClick={toggleLanguage}
            className="flex items-center gap-1 px-2 py-1 text-xs text-[#547055] border border-[#E6E2D6]"
            data-testid="mobile-language-toggle"
          >
            <Globe size={12} />
            {i18n.language === 'es' ? 'EN' : 'ES'}
          </button>
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="text-[#1C2E24] p-2"
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden glass mt-4"
            data-testid="mobile-menu"
          >
            <nav className="px-6 py-8 flex flex-col gap-6">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-lg ${
                    location.pathname === link.path
                      ? "text-[#1C2E24]"
                      : "text-[#547055]"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <a
                href={`https://wa.me/${SITE_CONFIG.whatsapp.number}?text=${encodeURIComponent(SITE_CONFIG.whatsapp.appointmentMessage)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary text-center mt-4"
              >
                {t('nav.agendarConsulta')}
              </a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
